<template>
  <div class="keyboard-shortcuts">
    <button
      ref="trigger"
      type="button"
      class="keyboard-shortcuts-trigger"
      :class="{ 'is-open': open }"
      title="Keyboard shortcuts"
      aria-label="Keyboard shortcuts"
      @click.stop="toggle"
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="2.5" y="6" width="19" height="12" rx="2.5" stroke="currentColor" stroke-width="1.6" />
        <path
          d="M6 9.5h.01M9 9.5h.01M12 9.5h.01M15 9.5h.01M18 9.5h.01M6 12.5h.01M9 12.5h.01M12 12.5h.01M15 12.5h.01M18 12.5h.01M8 15.25h8"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />
      </svg>
    </button>

    <!-- Fixed to the viewport so nothing in the top bar can clip it. -->
    <div v-if="open" class="shortcuts-panel" :style="panelStyle" @click.stop>
      <div class="sp-header">
        <span class="sp-header-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="2.5" y="6" width="19" height="12" rx="2.5" stroke="currentColor" stroke-width="1.6" />
            <path
              d="M6 9.5h.01M9 9.5h.01M12 9.5h.01M15 9.5h.01M18 9.5h.01M6 12.5h.01M9 12.5h.01M12 12.5h.01M15 12.5h.01M18 12.5h.01M8 15.25h8"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </span>
        <span class="sp-header-text">
          <span class="sp-title">Keyboard shortcuts</span>
          <span class="sp-subtitle">Switch tabs, search and open rows from your keyboard</span>
        </span>
        <button type="button" class="sp-x" aria-label="Close" @click="close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="sp-body">
        <p v-if="!anyAvailable" class="sp-alert">
          Shortcuts work on the list screens: Cases, Orders, Sendouts and
          Billing. Open one of them to use these.
        </p>

        <section class="sp-group" :class="{ 'is-unavailable': !tabs.available }">
          <div class="sp-group-head">
            <span class="sp-group-title">Switch tabs</span>
            <span v-if="!tabs.available" class="sp-badge">Not on this page</span>
          </div>
          <!-- A small picture of the tab strip: where each key takes you. -->
          <div class="sp-tabs-demo">
            <span class="sp-demo-tab" :title="demoTabs.previous">{{ demoTabs.previous }}</span>
            <span class="sp-demo-tab is-current" :title="demoTabs.current">{{ demoTabs.current }}</span>
            <span class="sp-demo-tab" :title="demoTabs.next">{{ demoTabs.next }}</span>
          </div>
          <div class="sp-tabs-keys">
            <span class="sp-tabs-key">
              <span class="sp-arrow">←</span>
              <span class="sp-keys"><kbd>Shift</kbd><span class="sp-plus">+</span><kbd>Tab</kbd></span>
              <span class="sp-tabs-key-label">Previous</span>
            </span>
            <span class="sp-tabs-key">
              <span class="sp-tabs-key-label">Next</span>
              <span class="sp-keys"><kbd>Tab</kbd></span>
              <span class="sp-arrow">→</span>
            </span>
          </div>
        </section>

        <section class="sp-group" :class="{ 'is-unavailable': !searchAvailable }">
          <div class="sp-group-head">
            <span class="sp-group-title">Search</span>
            <span v-if="!searchAvailable" class="sp-badge">Not on this page</span>
          </div>
          <div class="sp-row">
            <span class="sp-row-label">Jump to the search box</span>
            <span class="sp-keys"><kbd>{{ isMac ? "⌘" : "Ctrl" }}</kbd><kbd>K</kbd></span>
          </div>
        </section>

        <section class="sp-group" :class="{ 'is-unavailable': !rowsAvailable }">
          <div class="sp-group-head">
            <span class="sp-group-title">{{ rowWords.title }}</span>
            <span v-if="!rowsAvailable" class="sp-badge">Not on this page</span>
          </div>
          <div class="sp-row">
            <span class="sp-row-label">Move the highlight up or down</span>
            <span class="sp-keys"><kbd>↑</kbd><kbd>↓</kbd></span>
          </div>
          <div class="sp-row">
            <span class="sp-row-label">Open the highlighted {{ rowWords.noun }}</span>
            <span class="sp-keys"><kbd>Enter</kbd></span>
          </div>
          <div class="sp-row">
            <span class="sp-row-label">Tick the highlighted {{ rowWords.noun }}'s checkbox</span>
            <span class="sp-keys"><kbd>Space</kbd></span>
          </div>
          <div class="sp-row">
            <span class="sp-row-label">Clear the highlight</span>
            <span class="sp-keys"><kbd>Esc</kbd></span>
          </div>
        </section>
      </div>

      <div class="sp-footer">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.4" />
          <path d="M8 7.25v3.5M8 5.1v.05" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
        <span>Paused while you type in a field or a dialog is open.</span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getSwitchableTabs,
  isShortcutAvailable,
  getRowShortcutNoun,
  SHORTCUTS_CHANGED_EVENT,
} from "@/mixins/keyboardShortcutGuards";

// Bump the suffix when shortcuts are added, so everyone sees the panel once more.
const SEEN_KEY = "ls_keyboard_shortcuts_seen_v3";
const PANEL_WIDTH = 360;

// Keyboard icon in the top bar, beside notifications. Lists every shortcut and
// marks the ones the current page does not support.
export default {
  data() {
    return {
      open: false,
      panelStyle: {},
      tabs: { available: false, current: "", next: "", previous: "" },
      searchAvailable: false,
      rowsAvailable: false,
      rowNoun: "",
    };
  },
  computed: {
    isMac() {
      const platform =
        (typeof navigator !== "undefined" &&
          (navigator.userAgentData?.platform || navigator.platform)) ||
        "";
      return /mac|iphone|ipad/i.test(platform);
    },
    anyAvailable() {
      return this.tabs.available || this.searchAvailable || this.rowsAvailable;
    },
    // The list's own word for a row: "case", "order", "sendout".
    rowWords() {
      const noun = this.rowNoun || "row";
      const plural = `${noun}s`;
      return { noun, title: plural.charAt(0).toUpperCase() + plural.slice(1) };
    },
    // Real tab names when the page has tabs, a generic example otherwise.
    demoTabs() {
      return this.tabs.available
        ? this.tabs
        : { previous: "All", current: "Active", next: "Processing" };
    },
  },
  watch: {
    // Keeps the "From ... to ..." examples right while tabs are switched.
    "$route.path"() {
      this.$nextTick(this.refresh);
    },
  },
  mounted() {
    window.addEventListener("click", this.close);
    window.addEventListener("resize", this.close);
    window.addEventListener("keydown", this.onKeydown);
    window.addEventListener(SHORTCUTS_CHANGED_EVENT, this.onShortcutsChanged);
    this.$nextTick(this.onShortcutsChanged);
  },
  beforeUnmount() {
    window.removeEventListener("click", this.close);
    window.removeEventListener("resize", this.close);
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener(SHORTCUTS_CHANGED_EVENT, this.onShortcutsChanged);
  },
  methods: {
    refresh() {
      const { links, currentIndex } = getSwitchableTabs();
      if (currentIndex === -1) {
        this.tabs = { available: false, current: "", next: "", previous: "" };
      } else {
        const label = (link) => link.textContent.replace(/\s+/g, " ").trim();
        const at = (offset) =>
          links[(currentIndex + offset + links.length) % links.length];
        this.tabs = {
          available: true,
          current: label(links[currentIndex]),
          next: label(at(1)),
          previous: label(at(-1)),
        };
      }
      this.searchAvailable = isShortcutAvailable("search");
      this.rowsAvailable = isShortcutAvailable("rows");
      this.rowNoun = getRowShortcutNoun();
    },
    // A list screen has just appeared or gone.
    onShortcutsChanged() {
      this.refresh();
      this.openOnceOnAList();
    },
    // Opens by itself the first time a list is shown, so the shortcuts get discovered.
    openOnceOnAList() {
      if (!this.rowsAvailable && !this.searchAvailable) return;
      try {
        if (window.localStorage.getItem(SEEN_KEY)) return;
        window.localStorage.setItem(SEEN_KEY, "1");
      } catch (err) {
        return;
      }
      this.$nextTick(this.show);
    },
    show() {
      const trigger = this.$refs.trigger;
      if (!trigger) return;
      this.refresh();
      const rect = trigger.getBoundingClientRect();
      const left = Math.max(
        12,
        Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 12)
      );
      this.panelStyle = {
        top: `${rect.bottom + 12}px`,
        left: `${left}px`,
        width: `${PANEL_WIDTH}px`,
        maxHeight: `${Math.max(260, window.innerHeight - rect.bottom - 28)}px`,
      };
      this.open = true;
    },
    close() {
      this.open = false;
    },
    toggle() {
      if (this.open) this.close();
      else this.show();
    },
    onKeydown(event) {
      if (event.key === "Escape") this.close();
    },
  },
};
</script>

<style lang="scss">
.keyboard-shortcuts {
  display: inline-flex;
  align-items: center;

  .keyboard-shortcuts-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: #273480;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover,
    &.is-open {
      background: #eaedf9;
    }
  }
}

.shortcuts-panel {
  position: fixed;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid #e3e6ef;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 18px 44px rgba(23, 31, 77, 0.2), 0 2px 6px rgba(23, 31, 77, 0.06);
  color: #1f2a5c;
  font-size: 13px;
  line-height: 1.3;
  text-align: left;
  animation: sp-in 0.16s ease-out;

  @keyframes sp-in {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 24px;
    padding: 0 7px;
    box-sizing: border-box;
    border: 1px solid #cfd4e1;
    border-radius: 6px;
    background: linear-gradient(#ffffff, #f2f4f9);
    box-shadow: 0 1.5px 0 #cfd4e1;
    color: #374151;
    font-family: inherit;
    font-size: 11.5px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
  }
  .sp-keys {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 auto;
  }
  /* "+" between keys that are held together. */
  .sp-plus {
    color: #8a909c;
    font-size: 12px;
    font-weight: 600;
  }

  .sp-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 14px 14px 16px;
    background: linear-gradient(135deg, #273480 0%, #3b4bb0 100%);
    color: #fff;
  }
  .sp-header-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex: 0 0 auto;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.16);
  }
  .sp-header-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1 1 auto;
    min-width: 0;
  }
  .sp-title {
    font-size: 15px;
    font-weight: 600;
  }
  .sp-subtitle {
    font-size: 12px;
    opacity: 0.8;
  }
  .sp-x {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex: 0 0 auto;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #fff;
    opacity: 0.8;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.16);
      opacity: 1;
    }
  }

  .sp-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 14px 14px 4px;
    background: #f7f8fc;
  }
  .sp-alert {
    margin: 0 0 12px;
    padding: 9px 11px;
    border: 1px solid #f6e3b4;
    border-radius: 10px;
    background: #fff8e6;
    color: #7a5b12;
    font-size: 12px;
    line-height: 1.45;
  }
  .sp-group {
    margin-bottom: 10px;
    padding: 11px 12px 6px;
    border: 1px solid #e9ecf4;
    border-radius: 12px;
    background: #fff;

    &.is-unavailable {
      .sp-row,
      .sp-tabs-demo,
      .sp-tabs-keys {
        opacity: 0.45;
      }
    }
  }
  .sp-group-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .sp-group-title {
    color: #273480;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
  }
  .sp-badge {
    padding: 2px 8px;
    border-radius: 999px;
    background: #f1f2f6;
    color: #8a909c;
    font-size: 10px;
    font-weight: 500;
  }
  .sp-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 0;

    & + .sp-row {
      border-top: 1px solid #f1f3f8;
    }
  }
  .sp-row-label {
    color: #2b3352;
    font-size: 13px;
    font-weight: 500;
  }

  /* Miniature of the tab strip. */
  .sp-tabs-demo {
    display: flex;
    gap: 4px;
    padding: 4px;
    border-radius: 9px;
    background: #f1f3f9;
  }
  .sp-demo-tab {
    flex: 1 1 0;
    min-width: 0;
    padding: 6px 6px;
    overflow: hidden;
    border-radius: 6px;
    color: #8a909c;
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.3px;
    text-align: center;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;

    &.is-current {
      background: #fff;
      box-shadow: 0 1px 3px rgba(23, 31, 77, 0.14), inset 0 -2px 0 #273480;
      color: #273480;
      font-weight: 700;
    }
  }
  .sp-tabs-keys {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 2px 6px;
  }
  .sp-tabs-key {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .sp-tabs-key-label {
    color: #2b3352;
    font-size: 12.5px;
    font-weight: 500;
  }
  .sp-arrow {
    color: #273480;
    font-size: 14px;
    font-weight: 600;
  }

  .sp-footer {
    display: flex;
    align-items: center;
    gap: 7px;
    flex: 0 0 auto;
    padding: 10px 16px 12px;
    border-top: 1px solid #eef0f6;
    background: #fff;
    color: #7b8190;
    font-size: 11.5px;

    svg {
      flex: 0 0 auto;
    }
  }
}
</style>
